import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BottomSheet from '../mobile/BottomSheet'
import MaterialIcon from '../ui/MaterialIcon'
import { KILO } from '../../constants/KILO_THEME'
import type { AggregatedGroceryItem } from '../../lib/aggregateGroceryList'
import {
  fetchIngredientNutrition,
  scaleNutritionPer100g,
  type IngredientNutritionRow,
} from '../../lib/ingredientNutrition'
import { sanitizeCoachText } from '../../lib/aiResponseValidator'

type IngredientNutritionSheetProps = {
  open: boolean
  onClose: () => void
  item: AggregatedGroceryItem | null
  productName?: string | null
}

function MacroCell({ label, value }: { label: string; value: string }) {
  return (
    <div className={`rounded-xl border border-kilo-line bg-kilo-nested p-2.5 text-center`}>
      <p className={`text-sm font-bold ${KILO.text.primary}`}>{value}</p>
      <p className={`mt-0.5 text-[10px] ${KILO.text.muted}`}>{label}</p>
    </div>
  )
}

function nutriscoreClass(grade: string): string {
  const map: Record<string, string> = {
    a: 'bg-emerald-500/20 text-emerald-300',
    b: 'bg-lime-500/20 text-lime-300',
    c: 'bg-amber-500/20 text-amber-300',
    d: 'bg-orange-500/20 text-orange-300',
    e: 'bg-red-500/20 text-red-300',
  }
  return map[grade.toLowerCase()] ?? 'bg-kilo-nested text-kilo-text-muted'
}

export function IngredientNutritionSheet({
  open,
  onClose,
  item,
  productName,
}: IngredientNutritionSheetProps) {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [nutrition, setNutrition] = useState<IngredientNutritionRow | null>(null)

  useEffect(() => {
    if (!open || !item) {
      setNutrition(null)
      setError('')
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    setError('')

    void fetchIngredientNutrition(item.canonicalName).then(({ data, error: fetchError }) => {
      if (cancelled) return
      setLoading(false)
      if (fetchError) {
        setError(fetchError)
        return
      }
      if (!data) {
        setError(t('shopping.nutrition.noData'))
        return
      }
      setNutrition(data)
    })

    return () => {
      cancelled = true
    }
  }, [open, item, t])

  const weeklyGrams = useMemo(() => {
    if (!item?.shopping_pack) return null
    const { pack_unit, weekly_total } = item.shopping_pack
    // weekly_total is the exact protocol requirement (not the rounded-up purchase qty).
    // For stk items weekly_total is a unit count, not grams — skip scaling.
    if ((pack_unit === 'g' || pack_unit === 'ml') && weekly_total > 0) {
      return weekly_total
    }
    return null
  }, [item])

  const per100g = nutrition
    ? {
        kcal: Math.round(Number(nutrition.energy_kcal_100g ?? 0)),
        protein: Number(nutrition.protein_100g ?? 0),
        carbs: Number(nutrition.carbs_100g ?? 0),
        fat: Number(nutrition.fat_100g ?? 0),
        fiber: Number(nutrition.fiber_100g ?? 0),
        sugar: Number(nutrition.sugar_100g ?? 0),
        sodium: Number(nutrition.sodium_100g ?? 0),
      }
    : null

  const weeklyScaled = nutrition && weeklyGrams ? scaleNutritionPer100g(nutrition, weeklyGrams) : null

  const sourceLabel = (() => {
    if (!nutrition?.source) return null
    if (nutrition.source === 'open_food_facts') return t('shopping.nutrition.sourceOff')
    if (nutrition.source === 'usda') return t('shopping.nutrition.sourceUsda')
    return t('shopping.nutrition.sourceReference')
  })()

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={item?.canonicalName ?? t('shopping.nutrition.title')}
      variant="dark"
    >
      {item ? (
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            {nutrition?.image_url ? (
              <img
                src={nutrition.image_url}
                alt=""
                className="h-16 w-16 shrink-0 rounded-xl border border-kilo-line object-cover"
              />
            ) : (
              <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl border border-kilo-line bg-kilo-nested">
                <MaterialIcon name="nutrition" size="lg" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className={`text-sm ${KILO.text.muted}`}>
                {productName ?? item.shopping_pack?.label ?? item.quantity_mass}
              </p>
              {nutrition?.off_product_name && nutrition.off_product_name !== item.canonicalName ? (
                <p className={`mt-0.5 text-xs ${KILO.text.soft}`}>{nutrition.off_product_name}</p>
              ) : null}
              <div className="mt-2 flex flex-wrap gap-1.5">
                {nutrition?.nutriscore_grade ? (
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-mono uppercase ${nutriscoreClass(nutrition.nutriscore_grade)}`}
                  >
                    Nutri-Score {nutrition.nutriscore_grade.toUpperCase()}
                  </span>
                ) : null}
                {nutrition?.is_organic ? (
                  <span className="rounded-full bg-kilo-accent/15 px-2 py-0.5 text-[10px] font-mono uppercase text-kilo-accent">
                    {t('shopping.nutrition.organic')}
                  </span>
                ) : null}
                {nutrition?.primary_nutrient ? (
                  <span className={`rounded-full bg-kilo-nested px-2 py-0.5 text-[10px] font-mono uppercase ${KILO.text.muted}`}>
                    {t(`shopping.nutrition.primary.${nutrition.primary_nutrient}`, {
                      defaultValue: nutrition.primary_nutrient,
                    })}
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          {sanitizeCoachText(item.coaching_reason, 'coaching_reason') ? (
            <div className="rounded-xl border border-kilo-accent/20 bg-kilo-accent/5 px-3 py-2.5">
              <p className={`text-[10px] font-mono uppercase tracking-widest ${KILO.text.soft}`}>
                {t('shopping.nutrition.coachingWhy')}
              </p>
              <p className="mt-1 text-sm italic leading-snug text-kilo-accent/80">&ldquo;{sanitizeCoachText(item.coaching_reason, 'coaching_reason')}&rdquo;</p>
            </div>
          ) : null}

          {loading ? (
            <p className={`py-6 text-center text-sm ${KILO.text.muted}`}>{t('shopping.nutrition.loading')}</p>
          ) : error ? (
            <p className="py-4 text-center text-sm text-kilo-danger">{error}</p>
          ) : per100g ? (
            <>
              <div>
                <p className={`mb-2 ${KILO.fieldLabel}`}>{t('shopping.nutrition.per100g')}</p>
                <div className="grid grid-cols-4 gap-2">
                  <MacroCell label="kcal" value={String(per100g.kcal)} />
                  <MacroCell label={t('shopping.nutrition.protein')} value={`${per100g.protein}g`} />
                  <MacroCell label={t('shopping.nutrition.carbs')} value={`${per100g.carbs}g`} />
                  <MacroCell label={t('shopping.nutrition.fats')} value={`${per100g.fat}g`} />
                </div>
                {per100g.fiber > 0 || per100g.sugar > 0 || per100g.sodium > 0 ? (
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    {per100g.fiber > 0 ? (
                      <MacroCell label={t('shopping.nutrition.fiber')} value={`${per100g.fiber}g`} />
                    ) : null}
                    {per100g.sugar > 0 ? (
                      <MacroCell label={t('shopping.nutrition.sugar')} value={`${per100g.sugar}g`} />
                    ) : null}
                    {per100g.sodium > 0 ? (
                      <MacroCell label={t('shopping.nutrition.sodium')} value={`${per100g.sodium}g`} />
                    ) : null}
                  </div>
                ) : null}
              </div>

              {weeklyScaled && weeklyGrams ? (
                <div>
                  <p className={`mb-2 ${KILO.fieldLabel}`}>
                    {t('shopping.nutrition.forYourList', { label: item.shopping_pack?.label ?? item.quantity_mass })}
                  </p>
                  <div className="grid grid-cols-4 gap-2">
                    <MacroCell label="kcal" value={String(weeklyScaled.energy_kcal)} />
                    <MacroCell label={t('shopping.nutrition.protein')} value={`${weeklyScaled.protein_g}g`} />
                    <MacroCell label={t('shopping.nutrition.carbs')} value={`${weeklyScaled.carbs_g}g`} />
                    <MacroCell label={t('shopping.nutrition.fats')} value={`${weeklyScaled.fat_g}g`} />
                  </div>
                </div>
              ) : null}

              {sourceLabel ? (
                <p className={`text-center text-[11px] ${KILO.text.soft}`}>{sourceLabel}</p>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </BottomSheet>
  )
}

export default IngredientNutritionSheet
